import * as React from 'react';

export interface DeliveryIconProps extends React.SVGProps<SVGSVGElement> {
  className?: string;
}

/**
 * Icons extracted verbatim from the Noor.Merchant Figma file (Delivery
 * sub-app — the Доставка stat cards and the Отчеты operational-analytics cards).
 * Each glyph is the exact vector from the design; the hardcoded fills are
 * swapped for `currentColor` so callers control size (`size-*`) and tint. Every
 * glyph keeps its own native Figma viewBox (noted per-icon).
 */

/**
 * Time-quarter (clock with a quarter wedge) — the "Запланировано" stat tile and
 * the "Среднее время доставки" report metric (Figma "time quarter", 21.5×21.5).
 */
export function DeliveryTimeQuarterIcon({ className, ...props }: DeliveryIconProps) {
  return (
    <svg
      viewBox="0 0 21.5 21.5"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M10.75 0C16.6871 5.15406e-07 21.5 4.81294 21.5 10.75C21.5 16.6871 16.6871 21.5 10.75 21.5C10.6491 21.5 10.5485 21.4989 10.4482 21.4961C10.0342 21.4847 9.70741 21.1396 9.71875 20.7256C9.73018 20.3116 10.0753 19.9848 10.4893 19.9961C10.5758 19.9985 10.6629 20 10.75 20C15.8586 20 20 15.8586 20 10.75C20 5.64137 15.8586 1.5 10.75 1.5C5.64137 1.5 1.5 5.64137 1.5 10.75C1.5 10.8371 1.50152 10.9242 1.50391 11.0107C1.51521 11.4247 1.18839 11.7698 0.774414 11.7812C0.360436 11.7926 0.0153378 11.4658 0.00390625 11.0518C0.0011392 10.9515 0 10.8509 0 10.75C0 4.81294 4.81294 0 10.75 0ZM5.98047 19.5732C6.14987 19.1956 6.5929 19.0263 6.9707 19.1953C7.12813 19.2659 7.28827 19.3327 7.4502 19.3945C7.83712 19.5423 8.03056 19.9754 7.88281 20.3623C7.73503 20.7492 7.30196 20.9436 6.91504 20.7959C6.72665 20.7239 6.54053 20.6465 6.35742 20.5645C5.97961 20.395 5.8111 19.9511 5.98047 19.5732ZM2.96777 17.0752C3.2687 16.7906 3.7437 16.8036 4.02832 17.1045C4.14725 17.2302 4.26975 17.3527 4.39551 17.4717C4.69641 17.7563 4.70941 18.2313 4.4248 18.5322C4.14017 18.833 3.66514 18.8461 3.36426 18.5615C3.21841 18.4236 3.07642 18.2816 2.93848 18.1357C2.65392 17.8349 2.66702 17.3598 2.96777 17.0752ZM1.1377 13.6172C1.52462 13.4694 1.95768 13.6629 2.10547 14.0498C2.16732 14.2117 2.23413 14.3719 2.30469 14.5293C2.47367 14.9071 2.30441 15.3501 1.92676 15.5195C1.54885 15.6889 1.10503 15.5204 0.935547 15.1426C0.853483 14.9595 0.776059 14.7733 0.704102 14.585C0.556374 14.198 0.750782 13.765 1.1377 13.6172ZM10.75 8.75C11.8546 8.75 12.75 9.64543 12.75 10.75C12.75 11.5893 12.2329 12.3078 11.5 12.6045V14.75C11.5 15.1642 11.1642 15.5 10.75 15.5C10.3358 15.5 10 15.1642 10 14.75V12.6045C9.49869 12.4016 9.09845 12.0013 8.89551 11.5H4.75C4.33579 11.5 4 11.1642 4 10.75C4 10.3358 4.33579 10 4.75 10H8.89551C9.19221 9.26707 9.91067 8.75 10.75 8.75Z" />
    </svg>
  );
}

/**
 * Fast-delivery (van with speed lines) — the "В пути" stat tile
 * (Figma "fast delivery", 20×18).
 */
export function DeliveryFastDeliveryIcon({ className, ...props }: DeliveryIconProps) {
  return (
    <svg
      viewBox="0 0 20 18"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M5 14C6.10457 14 7 14.8954 7 16C7 17.1046 6.10457 18 5 18C3.89543 18 3 17.1046 3 16C3 15.958 3.00136 15.9163 3.00391 15.875C3.06845 14.8287 3.93742 14 5 14ZM16 14C17.1046 14 18 14.8954 18 16C18 17.1046 17.1046 18 16 18C14.8954 18 14 17.1046 14 16C14 14.8954 14.8954 14 16 14ZM7.5 0C9.70914 0 11.5 1.79086 11.5 4V16H8.5C8.5 14.067 6.933 12.5 5 12.5C3.34384 12.5 1.95669 13.6498 1.59277 15.1953C0.625202 14.4652 0 13.3055 0 12V4C0 1.79086 1.79086 0 4 0H7.5ZM15.209 4C15.7171 4 16.2065 4.19349 16.5771 4.54102L19.3682 7.15723C19.7714 7.53526 19.9999 8.06352 20 8.61621V14C20 14.5361 19.7887 15.0227 19.4453 15.3818C19.1534 13.7437 17.722 12.5 16 12.5C14.7268 12.5 13.6124 13.1799 13 14.1963V4H15.209ZM4 8.25C3.58579 8.25 3.25 8.58579 3.25 9C3.25 9.41421 3.58579 9.75 4 9.75H8C8.41421 9.75 8.75 9.41421 8.75 9C8.75 8.58579 8.41421 8.25 8 8.25H4ZM6 4.25C5.58579 4.25 5.25 4.58579 5.25 5C5.25 5.41421 5.58579 5.75 6 5.75H8C8.41421 5.75 8.75 5.41421 8.75 5C8.75 4.58579 8.41421 4.25 8 4.25H6Z" />
    </svg>
  );
}

/**
 * Delivery-time (van with a clock) — the "Ожидание курьера в точке" report
 * metric (Figma "delivery-time", 20×18).
 */
export function DeliveryTimeIcon({ className, ...props }: DeliveryIconProps) {
  return (
    <svg
      viewBox="0 0 20 18"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M5 14C6.10457 14 7 14.8954 7 16C7 17.1046 6.10457 18 5 18C3.89543 18 3 17.1046 3 16C3 15.958 3.00136 15.9163 3.00391 15.875C3.06845 14.8287 3.93742 14 5 14ZM16 14C17.1046 14 18 14.8954 18 16C18 17.1046 17.1046 18 16 18C14.8954 18 14 17.1046 14 16C14 14.8954 14.8954 14 16 14ZM7.5 0C9.70914 0 11.5 1.79086 11.5 4V16H8.5C8.5 14.067 6.933 12.5 5 12.5C3.34384 12.5 1.95669 13.6498 1.59277 15.1953C0.625202 14.4652 0 13.3055 0 12V4C0 1.79086 1.79086 0 4 0H7.5ZM15.209 4C15.7171 4 16.2065 4.19349 16.5771 4.54102L19.3682 7.15723C19.7714 7.53526 19.9999 8.06352 20 8.61621V14C20 14.5361 19.7887 15.0227 19.4453 15.3818C19.1534 13.7437 17.722 12.5 16 12.5C14.7268 12.5 13.6124 13.1799 13 14.1963V4H15.209ZM6 3.75C5.58579 3.75 5.25 4.08579 5.25 4.5V8C5.25 8.41421 5.58579 8.75 6 8.75H8.5C8.91421 8.75 9.25 8.41421 9.25 8C9.25 7.58579 8.91421 7.25 8.5 7.25H6.75V4.5C6.75 4.08579 6.41421 3.75 6 3.75Z" />
    </svg>
  );
}

/**
 * Tick-double (two overlapping checkmarks) — the "Завершено" stat tile
 * (Figma "tick-double", 21.5×10.76).
 */
export function DeliveryTickDoubleIcon({ className, ...props }: DeliveryIconProps) {
  return (
    <svg
      viewBox="0 0 21.5 10.7595"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.3075 1.25173C15.5846 0.943847 15.5596 0.46963 15.2518 0.192536C14.9439 -0.0845584 14.4697 -0.0595996 14.1926 0.248283L8.17759 6.93158L7.21855 6.16436C6.8951 5.9056 6.42313 5.95804 6.16438 6.28149C5.90562 6.60493 5.95806 7.0769 6.28151 7.33566L7.17244 8.04841L6.45496 8.84561C6.01199 9.3378 5.26204 9.39915 4.74497 8.98549L1.21855 6.16436C0.895102 5.9056 0.423133 5.95804 0.164376 6.28149C-0.0943811 6.60493 -0.0419401 7.0769 0.281506 7.33566L3.80793 10.1568C4.94549 11.0668 6.59536 10.9319 7.5699 9.84906L8.34573 8.98703L9.80793 10.1568C10.9455 11.0668 12.5954 10.9319 13.5699 9.84906L21.3075 1.25173C21.5846 0.943847 21.5596 0.46963 21.2518 0.192536C20.9439 -0.0845584 20.4697 -0.0595996 20.1926 0.248283L12.455 8.84561C12.012 9.3378 11.262 9.39915 10.745 8.98549L9.35087 7.87021L15.3075 1.25173Z"
      />
    </svg>
  );
}

/**
 * Remove-circle (X inside a circle) — the "Отменено" stat tile
 * (Figma "remove-circle", 20×20).
 */
export function DeliveryRemoveCircleIcon({ className, ...props }: DeliveryIconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10ZM13.3588 13.3587C13.0659 13.6516 12.591 13.6516 12.2981 13.3587L10 11.0606L7.7019 13.3587C7.40901 13.6516 6.93413 13.6516 6.64124 13.3587C6.34835 13.0658 6.34835 12.5909 6.64124 12.298L8.93935 9.99994L6.64125 7.70184C6.34836 7.40895 6.34836 6.93408 6.64125 6.64118C6.93415 6.34829 7.40902 6.34829 7.70191 6.64118L10 8.93928L12.2981 6.64119C12.591 6.3483 13.0659 6.3483 13.3588 6.64119C13.6516 6.93409 13.6516 7.40896 13.3588 7.70185L11.0607 9.99994L13.3588 12.298C13.6517 12.5909 13.6517 13.0658 13.3588 13.3587Z"
      />
    </svg>
  );
}

/**
 * Bar-chart (three ascending bars) — the "Среднее время приготовления" report
 * metric (Figma "bar chart", 20×18).
 */
export function DeliveryBarChartIcon({ className, ...props }: DeliveryIconProps) {
  return (
    <svg
      viewBox="0 0 20 18"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M2 8C3.10457 8 4 8.89543 4 10V16C4 17.1046 3.10457 18 2 18C0.895431 18 -1.82137e-08 17.1046 0 16V10C4.82823e-08 8.89543 0.895431 8 2 8ZM10 4C11.1046 4 12 4.89543 12 6V16C12 17.1046 11.1046 18 10 18C8.89543 18 8 17.1046 8 16V6C8 4.89543 8.89543 4 10 4ZM18 0C19.1046 4.82823e-08 20 0.895431 20 2V16C20 17.1046 19.1046 18 18 18C16.8954 18 16 17.1046 16 16V2C16 0.895431 16.8954 1.21374e-07 18 0Z" />
    </svg>
  );
}
